import {
  IconButton,
  Stack,
  Textarea,
  Text,
  Flex,
  Icon,
  Image,
  Spinner,
  useDisclosure,
  Button,
  Portal,
  Input,
  Dialog,
  Menu,
} from '@chakra-ui/react';
import {
  Book,
  ChevronDown,
  Edit2,
  List,
  PlusCircle,
  Send,
  Trash2,
  User,
  XCircle,
} from 'react-feather';
import { toaster } from '../ui/toaster';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import { useGetAIResponseMutation, useLazyGetNodeQuery } from '../../lib/api/GraphAPI';
import { useEffect, useRef, useState } from 'react';
import {
  addConversation,
  addMessage,
  clearConversation,
  removeConversation,
  renameConversation,
  setOpenConversationId,
} from '../../lib/state/slices/ProjectStateSlice';
import { useLocation } from 'react-router-dom';
import { Node } from '../../lib/api/APITypes';
import { Conversation, Message, SearchMode } from '../../lib/state/ViewTypes';
import { v4 as uuidv4 } from 'uuid';
import Markdown from 'react-markdown';
import { store } from '../../lib/state/Store';
import { Tooltip } from '../ui/tooltip';
import { theme } from '@chakra-ui/theme';

const ChatMessage = (props: { message: Message }) => {
  if (props.message.issuer === 'user') {
    return (
      <Flex direction={'column'}>
        <Flex direction={'row'} alignItems={'center'}>
          <Icon
            background={'blue.700'}
            color={'white'}
            borderRadius="50%"
            padding={'2px'}
            width={'16px'}
            height={'16px'}
          >
            <User />
          </Icon>
          <Text fontWeight={600} color={'blue.700'} marginStart={2}>
            You
          </Text>
        </Flex>
        <Text
          background={'blue.200'}
          paddingStart={2}
          paddingEnd={2}
          paddingTop={1}
          paddingBottom={1}
          fontWeight={400}
          fontSize={'sm'}
          color={'blue.800'}
          borderRadius="10px"
        >
          {props.message.content}
        </Text>
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'}>
        <Flex direction={'row'} alignItems={'center'}>
          <Image
            src={'/logo192.png'}
            width={'16px'}
            background={'blue.700'}
            color={'white'}
            borderRadius="50%"
          />
          <Text fontWeight={600} color={'ai'} marginStart={2}>
            Meshy
          </Text>
        </Flex>
        <div
          style={{
            fontWeight: 400,
            fontSize: '0.875rem',
            color: 'blue.800',
            borderRadius: '10px',
          }}
        >
          <Markdown>{props.message.content}</Markdown>
        </div>
      </Flex>
    );
  }
};
const RAGChat = () => {
  const dispatch = useAppDispatch();

  const [responseQuery, { isLoading, isError, error }] = useGetAIResponseMutation();

  const [typedMessage, setTypedMessage] = useState('');
  const [clearFunction, setClearFunction] = useState(() => () => {});
  const [newChatName, setNewChatName] = useState('');

  const [nodeQuery, nodeQueryResult, lastNodeQueryInfo] = useLazyGetNodeQuery();

  const openConversationId = useAppSelector((state) => state.viewReducer.openConversationId);
  const conversations = useAppSelector((state) => state.viewReducer.conversations);
  const clearChatDisclosure = useDisclosure();
  const deleteChatDisclosure = useDisclosure();
  const renameChatDisclosure = useDisclosure();

  const initialFocusRef = useRef(null);

  useEffect(() => {
    if (
      conversations.find((conversation) => conversation.id === openConversationId) === undefined
    ) {
      console.log('didnt fint convo ID, resetting it');
      dispatch(setOpenConversationId(conversations[0].id));
    }
  }, [openConversationId]);

  let msgs: Message[] = conversations.find((conversation) => conversation.id === openConversationId)
    ? conversations.find((conversation) => conversation.id === openConversationId)!.messages
    : [];

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getResponse = (msg: string) => {
    responseQuery({
      conversationId: openConversationId,
      history: [
        ...msgs,
        {
          conversationId: openConversationId,
          content: msg,
          issuer: 'user',
          datetime: new Date().getTime(),
        },
      ],
      contextIds: currentContext.map((node) => node.properties.id),
    })
      .then((result) => {
        if (!result.data) {
          toaster.create({
            title: 'Error',
            description: 'No response from server',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        dispatch(addMessage(result.data!));
      })
      .catch((error) => {
        console.log(error);
        toaster.create({
          title: 'Error',
          description: 'No response from server',
          type: 'error',
          duration: 9000,
          isClosable: true,
        });
      });
  };

  const submitMessage = () => {
    setTypedMessage('');
    dispatch(
      addMessage({
        conversationId: openConversationId,
        content: typedMessage,
        issuer: 'user',
        datetime: new Date().getTime(),
      }),
    );
    getResponse(typedMessage);
    clearFunction();
  };

  useEffect(() => {
    scrollToBottom();
  }, [msgs]);
  const location = useLocation();
  const [currentContext, setCurrentContext] = useState<Node[]>([]);

  const documentSearchType = useAppSelector((state) => state.viewReducer.documents.search.type);
  const documentResult = useAppSelector((state) => state.viewReducer.documents.result);

  useEffect(() => {
    if (location.pathname.includes('research')) {
      if (documentSearchType === 'CONTEXT') {
        setCurrentContext(documentResult.CONTEXT.data ? documentResult.CONTEXT.data : []);
      } else {
        setCurrentContext(documentResult.EXACT.data ? documentResult.EXACT.data : []);
      }
    } else if (/^\/saved_cases\/[^\/]+/.test(location.pathname)) {
      setCurrentContext([]);
      if (
        !store.getState().viewReducer.documents.structure.FOLDER[
          location.pathname.split('/saved_cases/')[1]
        ]
      ) {
        return;
      }
      for (let document_id of store.getState().viewReducer.documents.structure.FOLDER[
        location.pathname.split('/saved_cases/')[1]
      ].documents) {
        nodeQuery(document_id).then((result) => {
          if (result.error) {
            console.log(result.error);
            return;
          } else if (!result || !result.data) {
            console.log('No data');
            return;
          } else {
            setCurrentContext((state) => [...state, result.data!]);
          }
        });
      }
    } else if (/^\/graphs\/[^\/]+/.test(location.pathname)) {
      setCurrentContext([]);
      if (
        !store.getState().viewReducer.documents.structure.GRAPH[
          location.pathname.split('/graphs/')[1]
        ] ||
        !store.getState().viewReducer.documents.structure.GRAPH[
          location.pathname.split('/graphs/')[1]
        ].serializedGraph
      ) {
        return;
      }
      for (let document_id of store
        .getState()
        .viewReducer.documents.structure.GRAPH[
          location.pathname.split('/graphs/')[1]
        ].serializedGraph?.nodes?.map((node) => node.attributes.id)) {
        nodeQuery(document_id).then((result) => {
          if (result.error) {
            console.log(result.error);
            return;
          } else if (!result || !result.data) {
            console.log('No data');
            return;
          } else {
            setCurrentContext((state) => [...state, result.data!]);
          }
        });
      }
    } else {
      setCurrentContext([]);
    }
  }, [
    location.pathname,
    documentResult,
    documentSearchType,
    store.getState().viewReducer.documents.structure.FOLDER,
    store.getState().viewReducer.documents.structure.GRAPH,
  ]);

  useEffect(() => {
    if (isError) {
      toaster.create({
        title: 'Error while querying RAG Chat',
        description: `Something went wrong while querying AI response endpoint: ${error}`,
        type: 'error',
        closable: true,
      });
    }
  }, [isError]);

  return (
    <Stack direction={'column'} justifyContent={'space-between'} height={'100%'} maxWidth={'420px'}>
      <Flex
        direction={'row'}
        alignItems={'center'}
        padding={2}
        justifyContent={'space-between'}
        background={'theme.gray.100'}
      >
        <Menu.Root>
          <Menu.Trigger asChild>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: '50%',
                flex: 1,
                cursor: 'pointer',
              }}
            >
              <Text fontWeight={600} color={theme.colors.purple[700]} lineClamp={1}>
                {
                  conversations.find(
                    (conversation: Conversation) => conversation.id === openConversationId,
                  )?.name
                }
              </Text>
              <Icon as={ChevronDown} color={theme.colors.gray[600]} marginStart={2} marginEnd={2} />
            </div>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content sideOffset={5}>
                <Menu.Item
                  value="Rename"
                  cursor={'pointer'}
                  onClick={() => {
                    renameChatDisclosure.onOpen();
                  }}
                >
                  <Icon as={Edit2} marginEnd={2} />
                  Rename
                </Menu.Item>
                <Menu.Item
                  value="Delete"
                  cursor={'pointer'}
                  onClick={() => {
                    deleteChatDisclosure.onOpen();
                  }}
                >
                  <Icon as={Trash2} marginEnd={2} />
                  Delete
                </Menu.Item>
                <Menu.Item
                  value="Clear"
                  cursor={'pointer'}
                  onClick={() => {
                    clearChatDisclosure.onOpen();
                  }}
                >
                  <Icon as={XCircle} marginEnd={2} />
                  Clear
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        <Flex alignItems={'center'} marginStart={2} marginEnd={2} gap={2}>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Flex alignItems={'center'} gap={1} cursor={'pointer'}>
                <Icon as={List} color={'theme.gray.600'} />
                <Text fontWeight={400} color={'theme.gray.600'} fontSize={'xs'}>
                  Chat List
                </Text>
              </Flex>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  {conversations.map((conversation) => (
                    <Menu.Item
                      key={conversation.id}
                      value={conversation.id}
                      cursor={'pointer'}
                      onClick={() => {
                        dispatch(setOpenConversationId(conversation.id));
                      }}
                    >
                      {conversation.name}
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
          <Flex
            alignItems={'center'}
            gap={1}
            cursor={'pointer'}
            onClick={() => {
              let newConvoId = uuidv4();
              dispatch(
                addConversation({
                  id: newConvoId,
                  name: 'New Chat',
                  datetime: new Date().getTime(),
                  messages: [
                    {
                      issuer: 'system',
                      content:
                        'Hey, I am Meshy, your friendly virtual assistant. You can ask questions about the cases which are currently on screen.',
                      conversationId: newConvoId,
                      datetime: new Date().getTime(),
                    },
                  ],
                }),
              );
              dispatch(setOpenConversationId(newConvoId));
            }}
          >
            <Icon as={PlusCircle} color={'theme.gray.600'} />
            <Text fontWeight={400} color={'theme.gray.600'} fontSize={'xs'}>
              New Chat
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <div style={{ height: '100%', overflow: 'auto', scrollbarWidth: 'thin' }}>
        <Stack direction={'column'} padding={2}>
          {msgs.map((message) => (
            <ChatMessage key={message.datetime} message={message} />
          ))}
        </Stack>
        <div ref={messagesEndRef} /> {/* Invisible div to scroll to */}
      </div>
      {isLoading && (
        <Flex direction={'row'} alignItems={'center'}>
          <Spinner size="sm" />{' '}
          <Text fontWeight={400} fontSize={'sm'} marginStart={1}>
            Meshy is thinking
          </Text>
        </Flex>
      )}
      <Stack background={'gray.100'} padding={2}>
        <Text fontSize={'xs'} fontWeight={600} color={'gray.600'}>
          Current Context:{' '}
        </Text>
        <Flex
          direction={'row'}
          border={'1px'}
          borderColor={'primary.200'}
          borderRadius={'15px'}
          width={'-webkit-fit-content'}
          alignItems={'center'}
          padding={'2px'}
          paddingStart={'5px'}
          paddingEnd={'5px'}
        >
          <Icon as={Book} color={'primary'} flexShrink={0} />
          <Tooltip
            content={
              currentContext.length === 0
                ? 'Current context is empty. The assistent is not available on Graphs and Folders yet'
                : 'Ask questions about these cases: ' +
                  currentContext.map((node) => node.properties.label).join(', ')
            }
          >
            <Text
              fontSize={'xs'}
              fontWeight={700}
              color={'blue.700'}
              flexGrow={1}
              flexBasis={'0px'}
              lineClamp={2}
            >
              {currentContext
                .map((node) => node.properties.label?.split('CASE OF').at(-1))
                .join(', ')}
            </Text>
          </Tooltip>
          {currentContext.length > 1 && (
            <Text
              flexGrow={0}
              lineClamp={1}
              fontSize={'xs'}
              fontWeight={700}
              color={'primary'}
              flexShrink={0}
            >
              + {currentContext.length - 1} more
            </Text>
          )}
        </Flex>
        <Stack direction="row" alignItems="center">
          <Textarea
            disabled={isLoading}
            resize="none"
            placeholder={
              currentContext.length === 1
                ? 'Ask anything about the case you have selected...'
                : 'Ask anything about the cases you have selected...'
            }
            onKeyDown={(e) => e.key === 'Enter' && submitMessage()}
            onChange={(e) => setTypedMessage(e.target.value)}
            value={typedMessage}
            background={'white'}
            style={{ scrollbarWidth: 'thin' }}
            flex="1"
          />
          <IconButton
            disabled={isLoading}
            size="xs"
            background={'blue.700'}
            color={'white'}
            aria-label="Send"
            onClick={submitMessage}
          >
            <Send width={'16px'} height={'16px'} />
          </IconButton>
        </Stack>
      </Stack>
      <Dialog.Root open={clearChatDisclosure.open} onOpen={clearChatDisclosure.onOpen}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Clear Chat</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>Are you sure you want to clear the chat?</Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="ghost" onClick={clearChatDisclosure.onClose}>
                    Cancel
                  </Button>
                </Dialog.ActionTrigger>
                <Dialog.ActionTrigger asChild>
                  <Button
                    onClick={() => {
                      dispatch(clearConversation(openConversationId));
                      clearChatDisclosure.onClose();
                    }}
                  >
                    Clear
                  </Button>
                </Dialog.ActionTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
      <Dialog.Root open={deleteChatDisclosure.open} onOpen={deleteChatDisclosure.onOpen}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Delete Chat</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>Are you sure you want to delete the chat?</Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="ghost" onClick={deleteChatDisclosure.onClose}>
                    Cancel
                  </Button>
                </Dialog.ActionTrigger>
                <Dialog.ActionTrigger asChild>
                  <Button
                    onClick={() => {
                      if (conversations.length > 1) {
                        dispatch(removeConversation(openConversationId));
                        dispatch(setOpenConversationId(conversations[0].id));
                        deleteChatDisclosure.onClose();
                      } else {
                        toaster.create({
                          title: 'Error',
                          description: 'You cannot delete the last conversation',
                          status: 'error',
                          duration: 9000,
                          isClosable: true,
                        });
                        deleteChatDisclosure.onClose();
                      }
                    }}
                  >
                    Delete
                  </Button>
                </Dialog.ActionTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
      <Dialog.Root open={renameChatDisclosure.open} onOpen={renameChatDisclosure.onOpen}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Rename Chat</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Dialog.Description>
                  Enter a new name for{' '}
                  {
                    conversations.find((conversation) => conversation.id === openConversationId)
                      ?.name
                  }
                  :
                </Dialog.Description>
                <Input
                  type="text"
                  value={newChatName}
                  onChange={(e) => setNewChatName(e.target.value)}
                />
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="ghost" onClick={renameChatDisclosure.onClose}>
                    Cancel
                  </Button>
                </Dialog.ActionTrigger>
                <Dialog.ActionTrigger asChild>
                  <Button
                    onClick={() => {
                      if (newChatName.trim() === '') {
                        toaster.create({
                          title: 'Error',
                          description: 'Chat name cannot be empty',
                          status: 'error',
                          duration: 9000,
                          isClosable: true,
                        });
                        return;
                      } else {
                        dispatch(renameConversation({ id: openConversationId, name: newChatName }));
                        renameChatDisclosure.onClose();
                      }
                    }}
                  >
                    Rename
                  </Button>
                </Dialog.ActionTrigger>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  );
};

export default RAGChat;
